import { useState, useEffect, useCallback } from 'react';
import axios from "axios";
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import NavTab from '../components/NavTab/NavTab';
import ProductInformation from '../components/Product/ProductInformation';
import ProductImages from '../components/ProductImages';
import ProductOrders from '../components/ProductOrders';
import ProductStock from '../components/Product/ProductStock';
import ProductEquivalents from '../components/Product/ProductEquivalents';
import SectionCard from '../components/SectionCard/SectionCard';
import { useBreadcrumb } from '../layouts/BreadcrumbContext';
import FabGroup from "../components/FabGroup";
import EditProductModal from '../components/Modal/EditProductModal';
import { useProductActions } from '../hooks/useProductActions';
import {
    faShoppingBasket,
    faArrowDown,
    faArrowUp,
    faPlus,
    faCubes,
    faBell,
    faBoxesStacked,
    faBarcode,
    faTag,
    faImages,
    faReceipt,
    faPen,
    faRightLeft,
    faFlask,
    faIdCard,
} from '@fortawesome/free-solid-svg-icons';
import { transliterateToArabic } from '../utils/transliterateArabic';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';
import { eventBus } from '../bus/eventBus';

// Loading placeholder: the sheet's own layout in shimmer bars (.skel, Listing/Table.scss),
// reusing the real classes so it fills the page like the sheet does — header (+ barcode area),
// price band, the tab bar, then the "Aperçu" tab's two field panels with their real row counts
const SKEL_FIELD_COLUMNS = [
  [62, 44, 30, 54, 70, 32, 40, 48, 22, 36], // Informations générales: Dénomination … Code-barres
  [40, 85, 22, 22, 22, 30, 18, 22, 26],     // Informations cliniques: Composition … Substance psychoactive
];
const SKEL_SECTION_TITLES = [90, 100, 60, 70, 65];
const ProductSkeleton = () => (
  <div className="page-content page-content--product" aria-busy="true">
    <div className="product-info-wrapper">
      <div className="product-header-card">
        <div className="product-header-body">
          <span className="skel" style={{ width: 52, height: 52, borderRadius: 12, flexShrink: 0 }} />
          <div className="product-header-info" style={{ flex: 1 }}>
            <span className="skel skel--line" style={{ width: '38%', height: 18 }} />
            <span className="skel skel--line" style={{ width: '26%', marginTop: 10 }} />
            <span style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              {[90, 110, 120].map((w) => <span key={w} className="skel" style={{ width: w, height: 24, borderRadius: 999 }} />)}
            </span>
          </div>
          {/* barcode area on the right */}
          <span style={{ width: 140, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <span className="skel skel--line" style={{ width: 120 }} />
            <span className="skel skel--line" style={{ width: 70 }} />
          </span>
        </div>
        <div className="product-price-grid">
          {[0, 1, 2, 3, 4].map((i) => (
            // same card as the real one: icon tile, label, value, context line (+ gauge on the stock card)
            <div key={i} className="product-price-card">
              <span className="skel product-price-card__icon" />
              <div style={{ flex: 1 }}>
                <span className="skel skel--line" style={{ width: '60%', height: 10 }} />
                <span className="skel skel--line" style={{ width: '50%', height: 20, marginTop: 8 }} />
                {i === 2 && <span className="skel skel--line" style={{ width: '100%', height: 5, marginTop: 10 }} />}
                <span className="skel skel--line" style={{ width: '75%', height: 9, marginTop: 9 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className="navtab navtab--narrow product-navtab">
      <div className="navtab-sidebar">
        <nav className="navtab-nav">
          {SKEL_SECTION_TITLES.concat(70).map((w, i) => (
            <span key={i} className="navtab-link">
              <span className="skel" style={{ width: 18, height: 18, borderRadius: 5 }} />
              <span className="skel skel--line" style={{ width: w }} />
            </span>
          ))}
        </nav>
      </div>
      <div className="navtab-content">
        <div className="product-info-wrapper">
          <div className="product-col-layout">
            {SKEL_FIELD_COLUMNS.map((widths, col) => (
              <div key={col}>
                <div className="app-section-card">
                  <div className="app-section-card__title">
                    <span className="skel" style={{ width: 28, height: 28, borderRadius: 8, marginInlineEnd: 10 }} />
                    <span className="skel skel--line" style={{ width: 170 }} />
                  </div>
                  {widths.map((w, i) => (
                    <div key={i} className="product-field-row">
                      <span className="skel skel--line" style={{ width: 110, marginInlineEnd: 50 }} />
                      <span className="skel skel--line" style={{ width: `${w}%` }} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// panel title icon of the sheet's sections: brand blue, as on the Aperçu cards
const SECTION_ICON_BLUE = { background: '#eff6ff', color: '#2563eb' };

const ProductDetails = () => {
  const { t, i18n } = useTranslation();
  const { settings, hasRight } = useAuth();
  const canEdit = hasRight('PRODUCTS_EDIT');
  const isArabic = i18n.language === 'ar';
  const globalStockAlertThreshold = Number(settings?.stock_inventory_min_stock_alert) || 10;
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasStockHistory, setHasStockHistory] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sectionCounts, setSectionCounts] = useState({});
  // sheet tabs: "Aperçu" (the field panels) by default, then Similaires, Même DCI, Stock…
  const [activeTab, setActiveTab] = useState('overview');
  const { setTitle } = usePageTitle();
  const { setBreadcrumb } = useBreadcrumb();

  const setStockCount = useCallback((n) => setSectionCounts(prev => prev.productStock === n ? prev : { ...prev, productStock: n }), []);
  const setImagesCount = useCallback((n) => setSectionCounts(prev => prev.productImages === n ? prev : { ...prev, productImages: n }), []);
  const setOrdersCount = useCallback((n) => setSectionCounts(prev => prev.productOrders === n ? prev : { ...prev, productOrders: n }), []);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/v1/products/${id}`, {withCredentials: true});
      setProduct(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // A sale finalized from the cart while this sheet is open (it may contain this product):
  // reload the product in place — no loading screen — and remount the Stock / Ventes sections
  // (saleRefreshKey) so their batches and sales list are refetched too.
  const [saleRefreshKey, setSaleRefreshKey] = useState(0);
  useEffect(() => {
    const onOrderPlaced = () => {
      axios.get(`${API_BASE_URL}/api/v1/products/${id}`, { withCredentials: true })
        .then(({ data }) => setProduct(data))
        .catch((err) => console.error(err));
      setSaleRefreshKey((k) => k + 1);
    };
    eventBus.on('orderPlaced', onOrderPlaced);
    return () => eventBus.off('orderPlaced', onOrderPlaced);
  }, [id]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/v1/stock/orderable/${id}/batches`, {
      withCredentials: true,
      params: { page: 0, size: 1 },
    })
      .then(({ data }) => setHasStockHistory((data.totalElements ?? 0) > 0))
      .catch(() => setHasStockHistory(false));
  }, [id]);

  // "Similaires" (substitutable equivalents) and "Même DCI" — see ProductEquivalenceService.
  const [equivalents, setEquivalents] = useState({ similar: [], sameDci: [], sameDciTotal: 0 });
  const [equivalentsLoading, setEquivalentsLoading] = useState(true);
  useEffect(() => {
    // Rows link to other products, so `id` can change while a request is in flight — drop
    // a stale response rather than showing the previous product's equivalents.
    let cancelled = false;
    setEquivalentsLoading(true);
    axios.get(`${API_BASE_URL}/api/v1/products/${id}/equivalents`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setEquivalents(data); })
      .catch(() => { if (!cancelled) setEquivalents({ similar: [], sameDci: [], sameDciTotal: 0 }); })
      .finally(() => { if (!cancelled) setEquivalentsLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!product) return;
    const displayName = isArabic ? (product.nameAr || transliterateToArabic(product.name)) : product.name;
    setTitle(displayName);
    document.title = displayName;
    setBreadcrumb([
      { label: t('Products'), url: '/products' },
      { label: displayName }
    ]);
  }, [product, setTitle, setBreadcrumb, t, isArabic]);

  const { addToCart, addingToCartId, handleAddStock, handleViewStock, openQuickEdit, handleQuickStock, modals: productModals } =
    useProductActions({ onRefresh: fetchProduct });

  if (loading) {
    return <ProductSkeleton />;
  }

  if (!product) {
    return <div className="page-content text-muted p-4">{t('product.not_found')}</div>;
  }

  const menuItems = [
    {
      id: 'productSimilar', label: 'Similaires', icon: faRightLeft, defaultOpen: false, flush: true,
      count: equivalents.similar.length,
      component: (
        <ProductEquivalents
          products={equivalents.similar}
          loading={equivalentsLoading}
          referencePrice={product.publicPrice}
          source={equivalents.source}
          emptyMessage="Aucun équivalent (même principe actif, même dosage et même forme) dans le catalogue."
          onAddToCart={addToCart}
          addingToCartId={addingToCartId}
          onViewStock={handleViewStock}
        />
      ),
    },
    {
      id: 'productSameDci', label: 'Même DCI', icon: faFlask, defaultOpen: false, flush: true,
      count: equivalents.sameDciTotal,
      component: (
        <ProductEquivalents
          products={equivalents.sameDci}
          totalCount={equivalents.sameDciTotal}
          source={equivalents.source}
          loading={equivalentsLoading}
          emptyMessage={product.composition ? "Aucun autre médicament avec le même principe actif." : "Composition non renseignée pour ce produit."}
          onAddToCart={addToCart}
          addingToCartId={addingToCartId}
          onViewStock={handleViewStock}
        />
      ),
    },
    { id: 'productStock', label: t('Stock'), icon: faCubes, defaultOpen: false, flush: true, count: sectionCounts.productStock, component: <ProductStock key={`stock-${saleRefreshKey}`} product={product} onBatchUpdate={fetchProduct} onCountChange={setStockCount} /> },
    { id: 'productImages', label: t('product.images'), icon: faImages, defaultOpen: false, count: sectionCounts.productImages, component: <ProductImages product={product} productType="product" onCountChange={setImagesCount} /> },
    { id: 'productOrders', label: t('Sales'), icon: faReceipt, defaultOpen: false, flush: true, count: sectionCounts.productOrders, component: <ProductOrders key={`orders-${saleRefreshKey}`} product={product} onCountChange={setOrdersCount} /> },
  ];

  const tabs = [
    {
      id: 'overview', label: t('product.tab_overview'), icon: faIdCard,
      component: <ProductInformation product={product} part="fields" />,
    },
    // each section in a framed panel (title + blue icon + count), like the Aperçu cards — the
    // accordions used to provide that frame; flush sections (tables) run edge to edge inside it
    ...menuItems.map((item) => ({
      ...item,
      component: (
        <SectionCard
          className="product-section-panel"
          title={item.label}
          icon={item.icon}
          iconStyle={SECTION_ICON_BLUE}
          action={item.count ? <span className="product-section-panel__count">{item.count}</span> : null}
        >
          <div className={item.flush ? undefined : 'product-section-panel__body'}>{item.component}</div>
        </SectionCard>
      ),
    })),
  ];

  const buttons = [
    { id: 'cart',     label: t('Add to cart'),                  icon: faShoppingBasket, onClick: () => addToCart(id, product?.totalStock) },
    { id: 'lots',     label: t('View batches'),                 icon: faCubes,          onClick: () => handleViewStock(id, product?.name) },
    ...(canEdit ? [
      { id: 'edit',     label: t('Modifier le produit'),          icon: faPen,            onClick: () => setShowEditModal(true) },
      { id: 'stock',    label: t('Add to stock'),                 icon: faPlus,           onClick: () => handleAddStock(id) },
      { id: 'alert',    label: t('product.stock_alert_threshold'), icon: faBell,          onClick: () => openQuickEdit(id, 'minimumStock', t('product.stock_alert_threshold'), product.minimumStock > 0 ? product.minimumStock : globalStockAlertThreshold) },
      { id: 'maxStock', label: t('product.stock_max_threshold'),  icon: faBoxesStacked,  onClick: () => openQuickEdit(id, 'maximumStock', t('product.stock_max_threshold'), product.maximumStock > 0 ? product.maximumStock : '') },
      { id: 'barcode',  label: t('Edit barcode'),                 icon: faBarcode,        onClick: () => openQuickEdit(id, 'barcode', t('Barcode'), product.barcode) },
      { id: 'ppv',      label: t('product.edit_ppv_price'),       icon: faTag,            onClick: () => openQuickEdit(id, 'ppv', t('product.ppv_public_price'), product.publicPrice) },
      { id: 'pph',      label: t('product.edit_pph_price'),       icon: faTag,            onClick: () => openQuickEdit(id, 'pph', t('product.pph_hospital_price'), product.hospitalPrice) },
    ] : []),
  ];

  const qty = product.totalStock ?? 0;
  const stockStatus = qty > 3 ? 'ok' : qty > 0 ? 'low' : hasStockHistory ? 'out' : 'neutral';

  return (
    <div className={`page-content page-content--product page-content--product-${stockStatus}`}>
      <FabGroup
        buttons={buttons}
        quickAction={{
          icon: faShoppingBasket,
          label: t('Add to cart'),
          loading: addingToCartId === id,
          onClick: () => addToCart(id, product?.totalStock),
        }}
      />

      <ProductInformation
        product={product}
        part="header"
        onEditBarcode={canEdit ? () => openQuickEdit(id, 'barcode', 'Code-barres', product.barcode) : undefined}
        onEditPublicPrice={canEdit ? () => openQuickEdit(id, 'ppv', t('product.ppv_public_price'), product.publicPrice) : undefined}
        onEditHospitalPrice={canEdit ? () => openQuickEdit(id, 'pph', t('product.pph_hospital_price'), product.hospitalPrice) : undefined}
        onEditMinThreshold={canEdit ? () => openQuickEdit(id, 'minimumStock', t('product.stock_alert_threshold'), product.minimumStock > 0 ? product.minimumStock : globalStockAlertThreshold) : undefined}
        onEditMaxThreshold={canEdit ? () => openQuickEdit(id, 'maximumStock', t('product.stock_max_threshold'), product.maximumStock > 0 ? product.maximumStock : '') : undefined}
        onStockClick={() => setActiveTab('productStock')}
      />

      {/* left menu (same as the order / patient sheets) — every section stays mounted so the
          counts that sections report on load show before they're opened */}
      <NavTab
        menuItems={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="navtab--narrow product-navtab"
        keepMounted
      />

      {productModals}

      <EditProductModal
        product={product}
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={(updated) => setProduct(updated)}
      />
    </div>
  );
};

export default ProductDetails;
